import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Flame, Mail, User, Eye, EyeOff, Shield, KeyRound, CheckCircle } from "lucide-react";
import axios from "axios";

export default function LoginPage() {
  const { user } = useAuth();
  const [mode, setMode] = useState("main");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (user) window.location.replace("/");
  }, [user]);

  const handleEmailLogin = async () => {
    if (!email || !password) { setError("Compila tutti i campi"); return; }
    setSubmitting(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data?.session_token) {
        localStorage.setItem("bbcoach_token", res.data.session_token);
        const verify = localStorage.getItem("bbcoach_token");
        if (verify) window.location.href = "/";
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Errore di accesso");
      setSubmitting(false);
    }
  };

  const handleRegister = async () => {
    if (!email || !password || !name) { setError("Compila tutti i campi"); return; }
    if (password.length < 4) { setError("Password minimo 4 caratteri"); return; }
    setSubmitting(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/register`, { email, password, name });
      if (res.data?.session_token) {
        localStorage.setItem("bbcoach_token", res.data.session_token);
        const verify = localStorage.getItem("bbcoach_token");
        if (verify) window.location.href = "/";
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Errore di registrazione");
      setSubmitting(false);
    }
  };

  const handleGuest = async () => {
    setSubmitting(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/guest`);
      if (res.data?.session_token) {
        localStorage.setItem("bbcoach_token", res.data.session_token);
        const verify = localStorage.getItem("bbcoach_token");
        if (verify) window.location.href = "/";
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Errore");
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) { setError("Inserisci la tua email"); return; }
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore nell'invio");
      setSubmitting(false);
    }
  };

  const inputClass = "w-full bg-black/50 border border-slate-800 focus:border-red-500/50 text-slate-200 placeholder:text-slate-600 py-3 px-4 rounded-sm transition-all outline-none focus:ring-1 focus:ring-red-500/20 shadow-inner text-sm";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
         style={{ background: "radial-gradient(circle at 50% 30%, #1a0505 0%, #020617 70%)" }}>

      {/* Background atmospheric effects */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-red-900/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-amber-900/10 rounded-full blur-[100px] pointer-events-none" />

      {/* Main container - Obsidian Tablet */}
      <div className="relative z-10 w-full max-w-lg">

        {/* Banner Image */}
        <div className="animate-fade-in mb-0">
          <img
            src="https://customer-assets.emergentagent.com/job_blood-bowl-roster/artifacts/10ea1krd_Gemini_Generated_Image_bw0e1pbw0e1pbw0e.png"
            alt="Doppioteschio - Blood Bowl"
            className="w-full rounded-t-sm border-x border-t border-slate-800/50 shadow-2xl shadow-red-900/20 opacity-90 hover:opacity-100 transition-opacity duration-700"
            data-testid="login-banner"
          />
        </div>

        {/* Card body */}
        <div className="bg-[#0a0a0a]/90 border-x border-b border-slate-800/50 border-t-0 backdrop-blur-sm p-6 md:p-10 rounded-b-sm shadow-2xl animate-fade-in stagger-1">

          {/* Amber accent line at top */}
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-amber-600/40 to-transparent" style={{ top: 'auto', position: 'relative', marginTop: '-1px', marginBottom: '24px' }} />

          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-[2.8rem] font-title tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-slate-400 leading-tight"
                style={{ filter: "drop-shadow(0 2px 10px rgba(225,29,72,0.3))" }}>
              DOPPIOTESCHIO
            </h1>
            <p className="text-xs md:text-sm font-bold tracking-[0.3em] text-red-500 uppercase mt-1">
              BBCoach - Blood Bowl Manager 2025
            </p>
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              Gestisci i tuoi team di Blood Bowl.<br />Traccia SPP, abilita e infortuni. Gioca in lega.
            </p>
          </div>

          {/* Decorative divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-amber-600/30" />
            <Shield className="w-4 h-4 text-amber-600/40" />
            <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-amber-600/30" />
          </div>

          {/* AUTH SECTION */}
          {mode === "main" && (
            <div className="space-y-3 animate-fade-in">
              {/* Email */}
              <button data-testid="email-login-btn" onClick={() => setMode("login")}
                className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 text-white font-bold uppercase tracking-widest py-3.5 px-6 rounded-sm shadow-[0_4px_14px_0_rgba(220,38,38,0.3)] transition-all hover:-translate-y-0.5 active:scale-[0.98] border border-red-400/20 touch-target text-sm">
                <Mail className="w-5 h-5" /> Accedi con Email
              </button>

              {/* Register */}
              <button data-testid="register-btn" onClick={() => setMode("register")}
                className="w-full flex items-center justify-center gap-3 bg-transparent border border-red-700/50 hover:border-red-500/70 text-red-400 hover:text-red-300 font-bold uppercase tracking-widest py-3.5 px-6 rounded-sm transition-all hover:bg-red-900/10 active:scale-[0.98] touch-target text-sm">
                <Mail className="w-5 h-5" /> Registrati
              </button>

              {/* Separator */}
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-[1px] bg-slate-800" />
                <span className="text-[10px] text-slate-600 uppercase tracking-wider">oppure</span>
                <div className="flex-1 h-[1px] bg-slate-800" />
              </div>

              {/* Guest */}
              <button data-testid="guest-login-btn" onClick={handleGuest} disabled={submitting}
                className="w-full flex items-center justify-center gap-3 bg-transparent border border-slate-700 hover:border-amber-500/50 text-slate-400 hover:text-amber-400 font-bold uppercase tracking-widest py-3.5 px-6 rounded-sm transition-all hover:bg-slate-900/50 active:scale-[0.98] touch-target text-sm disabled:opacity-40">
                <User className="w-5 h-5" /> {submitting ? "Accesso..." : "Entra come Ospite"}
              </button>
            </div>
          )}

          {mode === "login" && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="coach@example.com"
                  className={`${inputClass} mt-1.5`} data-testid="email-input" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Password</label>
                <div className="relative mt-1.5">
                  <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="La tua password"
                    className={`${inputClass} pr-10`} data-testid="password-input"
                    onKeyDown={(e) => { if (e.key === "Enter") handleEmailLogin(); }} />
                  <button onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400 transition-colors">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {error && <p className="text-xs text-red-400 font-bold" data-testid="auth-error">{error}</p>}
              <button data-testid="submit-login" onClick={handleEmailLogin} disabled={submitting}
                className="w-full bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 text-white font-bold uppercase tracking-widest py-3.5 rounded-sm shadow-[0_4px_14px_0_rgba(220,38,38,0.3)] transition-all hover:-translate-y-0.5 active:scale-[0.98] border border-red-400/20 disabled:opacity-40 touch-target text-sm">
                {submitting ? "Accesso..." : "Accedi"}
              </button>
              <div className="flex items-center justify-between pt-1">
                <button onClick={() => { setMode("register"); setError(""); }} className="text-xs text-red-400/80 font-bold hover:text-red-400 transition-colors" data-testid="go-register">Non hai un account? Registrati</button>
                <button onClick={() => { setMode("main"); setError(""); }} className="text-xs text-slate-600 hover:text-slate-400 transition-colors">Indietro</button>
              </div>
              <div className="text-center">
                <button onClick={() => { setMode("forgot"); setError(""); setForgotSent(false); }} className="text-[11px] text-slate-500 hover:text-amber-400 transition-colors" data-testid="forgot-password-link">
                  <KeyRound className="w-3 h-3 inline mr-1" />Password dimenticata?
                </button>
              </div>
            </div>
          )}

          {mode === "forgot" && (
            <div className="space-y-4 animate-fade-in">
              {forgotSent ? (
                <div className="text-center space-y-3" data-testid="forgot-sent-message">
                  <CheckCircle className="w-10 h-10 text-green-400 mx-auto" />
                  <h3 className="text-sm font-bold text-white">Email inviata!</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">Se l'indirizzo e associato a un account, riceverai un link per reimpostare la password. Controlla anche la cartella spam.</p>
                  <button onClick={() => { setMode("login"); setError(""); setForgotSent(false); }}
                    className="text-xs text-red-400/80 font-bold hover:text-red-400 transition-colors mt-2" data-testid="back-to-login-after-forgot">
                    Torna al Login
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-center">
                    <KeyRound className="w-8 h-8 text-amber-500/60 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">Inserisci la tua email e ti invieremo un link per reimpostare la password.</p>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Email</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="coach@example.com"
                      className={`${inputClass} mt-1.5`} data-testid="forgot-email-input"
                      onKeyDown={(e) => { if (e.key === "Enter") handleForgotPassword(); }} />
                  </div>
                  {error && <p className="text-xs text-red-400 font-bold" data-testid="auth-error">{error}</p>}
                  <button data-testid="submit-forgot" onClick={handleForgotPassword} disabled={submitting}
                    className="w-full bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-600 hover:to-amber-500 text-white font-bold uppercase tracking-widest py-3.5 rounded-sm shadow-[0_4px_14px_0_rgba(217,119,6,0.3)] transition-all hover:-translate-y-0.5 active:scale-[0.98] border border-amber-400/20 disabled:opacity-40 touch-target text-sm">
                    {submitting ? "Invio..." : "Invia Link Reset"}
                  </button>
                  <div className="text-center pt-1">
                    <button onClick={() => { setMode("login"); setError(""); }} className="text-xs text-slate-600 hover:text-slate-400 transition-colors">Torna al Login</button>
                  </div>
                </>
              )}
            </div>
          )}

          {mode === "register" && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Nome Coach</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Il tuo nome da Coach"
                  className={`${inputClass} mt-1.5`} data-testid="name-input" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="coach@example.com"
                  className={`${inputClass} mt-1.5`} data-testid="reg-email-input" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Password</label>
                <div className="relative mt-1.5">
                  <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimo 4 caratteri"
                    className={`${inputClass} pr-10`} data-testid="reg-password-input"
                    onKeyDown={(e) => { if (e.key === "Enter") handleRegister(); }} />
                  <button onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400 transition-colors">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {error && <p className="text-xs text-red-400 font-bold" data-testid="auth-error">{error}</p>}
              <button data-testid="submit-register" onClick={handleRegister} disabled={submitting}
                className="w-full bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 text-white font-bold uppercase tracking-widest py-3.5 rounded-sm shadow-[0_4px_14px_0_rgba(220,38,38,0.3)] transition-all hover:-translate-y-0.5 active:scale-[0.98] border border-red-400/20 disabled:opacity-40 touch-target text-sm">
                {submitting ? "Registrazione..." : "Registrati"}
              </button>
              <div className="flex items-center justify-between pt-1">
                <button onClick={() => { setMode("login"); setError(""); }} className="text-xs text-red-400/80 font-bold hover:text-red-400 transition-colors" data-testid="go-login">Hai gia un account? Accedi</button>
                <button onClick={() => { setMode("main"); setError(""); }} className="text-xs text-slate-600 hover:text-slate-400 transition-colors">Indietro</button>
              </div>
            </div>
          )}
        </div>

        {/* Donation Banner */}
        <div className="mt-5 bg-black/40 backdrop-blur-sm border border-slate-800/50 rounded-sm p-3 text-center animate-fade-in stagger-3" data-testid="donation-banner-login">
          <p className="text-[10px] text-slate-500 mb-2">Ti piace Doppioteschio? Supporta lo sviluppo!</p>
          <a href="https://ko-fi.com/coachgabro" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-[#FF5E5B] text-white text-[10px] font-bold uppercase tracking-wider py-1.5 px-3 rounded-sm hover:brightness-110 transition-all" data-testid="kofi-link-login">
            <Flame className="w-3 h-3" /> Ko-fi
          </a>
        </div>

        {/* Credits */}
        <p className="text-[10px] text-slate-600 mt-4 text-center animate-fade-in stagger-4">
          Creata da <span className="text-slate-500">Coach Gabro Losco 'n Fitto</span>
        </p>
        <p className="text-[9px] text-slate-700 mt-1 text-center animate-fade-in stagger-4">
          Blood Bowl &copy; Games Workshop. Questa app non e affiliata.
        </p>
      </div>
    </div>
  );
}
